import { localize } from '../i18n';
import React, { useEffect, useState } from 'react';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PermissionNotice from './PermissionNotice';
import usePermissionMedications from '../hooks/usePermissionMedications';
import useIosRingtoneMode from '../pages/medication-form/reminder/useIosRingtoneMode';
import { readAlertRuntimeInfo, readNativePlatform } from '../utils/alertMethodSupport';
import { isMedicationAlertTarget } from '../utils/medicationAlertTarget';
import WebviewMessenger from '../utils/WebviewMessenger';
import FeatureUpdateDialog from './FeatureUpdateDialog';

export default function IosRingtonePermissionBanner({ medications = null, onOpenAlertSettings }) {
    const [runtime, setRuntime] = useState(readAlertRuntimeInfo);
    const [updateOpen, setUpdateOpen] = useState(false);
    const platform = readNativePlatform(runtime);
    const { medications: rows, error: medicationError, reload } = usePermissionMedications(platform === 'ios' ? medications : []);
    const needed = rows?.some(m => isMedicationAlertTarget(m) && m.alertType === 'FULLSCREEN');
    const supported = needed && runtime?.capabilities?.iosRingtoneMode === true;
    const { mode, busy, statusError, error, permissionHelp, query } = useIosRingtoneMode(platform, supported);
    useEffect(() => {
        const update = e => setRuntime(e.detail || readAlertRuntimeInfo());
        window.addEventListener('app-runtime-info-updated', update);
        return () => window.removeEventListener('app-runtime-info-updated', update);
    }, []);
    if (platform !== 'ios' || !window.ReactNativeWebView) return null;
    if (medicationError) return <PermissionNotice icon={<NotificationsActiveOutlinedIcon />} title={localize("약 정보 확인 필요")}
        description={localize("알림 대상 약을 불러오지 못했어요.")} actionLabel={localize("다시 확인")} onAction={reload} />;
    if (needed && !supported) return <>
        <PermissionNotice icon={<NotificationsActiveOutlinedIcon />} title={localize("벨소리 권한 확인 필요")}
            description={localize("앱을 업데이트해 확인해 주세요.")} actionLabel={localize("업데이트")} onAction={() => setUpdateOpen(true)} />
        <FeatureUpdateDialog open={updateOpen} onClose={() => setUpdateOpen(false)} platform="ios" />
    </>;
    if (!supported || busy) return null;
    const alarmNeeded = mode?.authorized === false && (mode.useAlarmKit || permissionHelp);
    const criticalNeeded = mode?.useAlarmKit === false && mode.criticalGranted === false && mode.criticalSupported !== false;
    const unsupported = mode?.useAlarmKit === false && mode.criticalSupported === false;
    const notice = statusError ? { title: localize('벨소리 권한 확인 필요'), description: localize('권한을 확인하지 못했어요.'), actionLabel: localize('다시 확인'), onAction: query }
        : alarmNeeded ? { title: localize('알람 권한이 필요해요'), description: localize('벨소리 알람을 허용해 주세요.') }
        : criticalNeeded ? { title: localize('긴급 알림 권한이 필요해요'), description: localize('무음에서도 울리려면 허용해 주세요.') }
        : unsupported ? { title: localize('긴급 알림 사용 불가'), description: localize('약 알림 설정에서 방식을 바꿔 주세요.'), actionLabel: localize('알림 설정'), onAction: onOpenAlertSettings }
        : mode?.pending || error ? { title: localize('벨소리 설정 확인 필요'), description: localize('알림 설정을 다시 적용해 주세요.'), actionLabel: localize('알림 설정'), onAction: onOpenAlertSettings }
        : null;
    if (!notice) return null;
    return <PermissionNotice icon={<NotificationsActiveOutlinedIcon />} actionLabel={localize("설정 열기")}
        onAction={() => WebviewMessenger.send({ type: 'OPEN_IOS_NOTIFICATION_SETTINGS' })} {...notice} />;
}
